import { useEffect, useRef, useState } from 'react'
import { useController, UseControllerProps, useForm } from 'react-hook-form'

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

import { SelectInterface } from '../../interfaces/InterfacesLocal'
import { countries } from '../../utils/countries'
import ComboBox from './Combobox'

interface InputProps {
  label: string
  placeholder?: string
  showInline?: boolean
  disabled?: boolean
  disabledMeasure?: boolean
  disabledValue?: boolean
  valueDefault?: {
    phoneNumber?: number
    countryCode?: number
  }
  value?: number
  control: string
}

const PhoneInput = (props: UseControllerProps & InputProps) => {
  const {
    label,
    placeholder,
    showInline,
    disabled,
    disabledValue,
    valueDefault,
  } = props
  const { field, fieldState } = useController(props)

  const countriesData = useRef<SelectInterface[]>(
    countries.map((item) => ({
      id: item.code,
      name: `${item.name} +(${item.code})`,
      displayName: `${item.textCode} +${item.code}`,
    }))
  )

  const [input, setInput] = useState<{
    phoneNumber?: number
    countryCode?: number
  }>(valueDefault ?? { phoneNumber: 0, countryCode: 53 })

  useEffect(() => {
    if (
      (!field.value || !field.value.phoneNumber) &&
      valueDefault?.phoneNumber
    ) {
      setInput(valueDefault)
      field.onChange(valueDefault)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    control: controlLocal,
    setError: setLocalError,
    clearErrors: clearLocalErrors,
  } = useForm()
  const isCountryCodeValid =
    input.countryCode != null &&
    countriesData.current.some((c) => c.id === input.countryCode)
  return (
    <div
      className={`${showInline ? 'inline-flex items-center gap-3' : 'flex-col'}`}
    >
      <label
        className={`block text-sm font-medium ${disabled || disabledValue ? 'text-gray-400' : 'text-gray-700'}`}
        htmlFor={label}
      >
        <span className="inline-flex items-center">
          {label}
          {(disabled || disabledValue) && (
            <LockClosedIcon className="h-4 px-2" />
          )}
        </span>
      </label>
      <div className="relative mt-1 flex rounded-md shadow-sm">
        <ComboBox
          className={'w-36'}
          control={controlLocal}
          data={countriesData.current}
          defaultValue={input.countryCode}
          inputClassName={'rounded-r-none pr-0 text-center'}
          label={''}
          name={'code'}
          onChangeValue={(value) => {
            const selectedCode = Number(value)
            const isValid = countriesData.current.some(
              (c) => c.id === selectedCode
            )
            if (!isValid) {
              setLocalError('code', {
                type: 'validate',
                message: 'Código de país inválido',
              })
              return
            }
            clearLocalErrors('code')
            const updated = {
              ...input,
              countryCode: selectedCode,
            }
            setInput(updated)
            field.onChange(updated)
          }}
          optionsClassName={' w-48 -translate-x-[2rem]'}
          placeholder={''}
          rules={{
            required: 'Seleccione un país',
            validate: (value) => {
              const selectedCode = Number(value)
              const isValid = countriesData.current.some(
                (c) => c.id === selectedCode
              )
              return isValid || 'Código de país inválido'
            },
          }}
          showLabel={false}
        />

        <input
          className={
            (fieldState.error
              ? 'border-red-300 text-red-900 placeholder-red-300 ' +
                'focus:border-red-500 focus:ring-red-500'
              : 'focus:border-gray-600 focus:ring-gray-500 ' +
                (disabled || disabledValue
                  ? 'border-gray-300 text-gray-400'
                  : 'border-gray-500 text-gray-500')) +
            ' block w-full rounded-r-md border-l-transparent pr-10 placeholder:text-slate-400 sm:text-sm'
          }
          disabled={disabled || disabledValue || !isCountryCodeValid}
          min={0}
          onChange={(e) => {
            const updated = {
              ...input,
              phoneNumber: Number(e.target.value),
            }
            setInput(updated)
            field.onChange(updated)
          }}
          placeholder={placeholder && placeholder}
          type="tel"
          value={input.phoneNumber === 0 ? '' : input.phoneNumber}
        />
        {fieldState.error && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 top-2 pr-3">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-5 w-5 text-red-500"
              />
            </div>

            <p className="absolute -bottom-5 flex flex-shrink-0 text-xs text-red-600">
              {fieldState.error?.message}
            </p>
          </>
        )}
        {/* <div className="absolute right-0 top-0 flex items-center gap-1 pr-0">
          {fieldState.error && (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          )}
        </div>
        {fieldState.error && (
          <p className="absolute mt-1 text-xs text-red-600">
            {fieldState.error?.message}
          </p>
        )} */}
      </div>
    </div>
  )
}

export default PhoneInput
